/**
 * First-party, opt-in consent state for non-essential tracking (analytics +
 * session replay). The goal is the CIPA/CCPA posture: nothing non-essential
 * loads until the visitor opts in, the browser's Global Privacy Control signal
 * is honored as an opt-out, and the choice is changeable via "Your Privacy
 * Choices".
 *
 * Strictly necessary things (cart/region storage, Stripe at checkout, Sentry
 * error capture without replay) are NOT gated here.
 */

export type ConsentValue = "granted" | "denied"

export type ConsentState = {
  /** Whether analytics + session replay (GA4, Sentry Replay) may load. */
  analytics: ConsentValue
  /** True once the visitor has made a choice OR GPC decided it for them. */
  decided: boolean
  /** True when the denial was forced by the browser's GPC signal. */
  gpc: boolean
}

const STORAGE_KEY = "plusbake_consent_v1"
const CHANGE_EVENT = "plusbake:consent-change"
export const OPEN_PRIVACY_CHOICES_EVENT = "plusbake:open-privacy-choices"

const isBrowser = () => typeof window !== "undefined"

const gpcEnabled = () =>
  isBrowser() &&
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true

const DENIED_UNSET: ConsentState = {
  analytics: "denied",
  decided: false,
  gpc: false,
}

export const getConsent = (): ConsentState => {
  if (!isBrowser()) return DENIED_UNSET

  // GPC is a legally recognized opt-out signal — treat it as a decided denial
  // that the visitor cannot be nagged about and that overrides a stored grant.
  if (gpcEnabled()) {
    return { analytics: "denied", decided: true, gpc: true }
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DENIED_UNSET
    const parsed = JSON.parse(raw) as { analytics?: unknown }
    return {
      analytics: parsed.analytics === "granted" ? "granted" : "denied",
      decided: true,
      gpc: false,
    }
  } catch {
    return DENIED_UNSET
  }
}

export const analyticsAllowed = (): boolean =>
  getConsent().analytics === "granted"

export const setConsent = (analytics: ConsentValue): void => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ analytics, decidedAt: new Date().toISOString() }),
    )
  } catch {
    // Storage unavailable (private mode/full) — the in-memory event below still
    // drives the current page; we simply can't persist the choice.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: getConsent() }))
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export const onConsentChange = (
  listener: (state: ConsentState) => void,
): (() => void) => {
  if (!isBrowser()) return () => {}
  const handler = () => listener(getConsent())
  window.addEventListener(CHANGE_EVENT, handler)
  return () => window.removeEventListener(CHANGE_EVENT, handler)
}

/** Re-open the consent dialog (wired to the footer "Your Privacy Choices"). */
export const openPrivacyChoices = (): void => {
  if (!isBrowser()) return
  window.dispatchEvent(new CustomEvent(OPEN_PRIVACY_CHOICES_EVENT))
}
